import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import Icon from "@mdi/react";
import {
	mdiOpenInNew,
	mdiEmailOutline,
	mdiCalendarBlankOutline,
	mdiFileOutline,
	mdiNoteEditOutline,
	mdiSlack,
	mdiFacebook,
	mdiLinkedin,
	mdiCreditCardOutline,
	mdiPalette,
	mdiImageMultipleOutline,
	mdiViewColumnOutline,
	mdiFilmstripBoxMultiple,
	mdiYoutube,
	mdiTextBoxOutline,
	mdiCheckCircleOutline,
	mdiCheckBold,
	mdiClose,
	mdiPencilOutline,
	mdiUndoVariant,
} from "@mdi/js";
import {
	NotistackServices,
	TaskDeliverableDTO,
	DeliverableReviewAction,
	TeamLeaderServices,
	DeliverableDiffDTO,
	FieldDiff,
} from "shared/services";
import { HandleError } from "shared/utils";
import styles from "./DeliverableList.module.scss";

interface DeliverableListProps {
	workspaceId: string;
	taskId: string;
	subtaskId: string;
	/**
	 * Quando muda, força refetch (ex.: ao receber WS update da subtarefa).
	 */
	refreshKey?: any;
}

// Google MCPs (gmail/calendar/drive) descontinuados em 2026-06-08 — substituidos
// pelos equivalentes Microsoft 365 (outlook/mscalendar/onedrive/teams).
const ICON_BY_MCP: Record<string, string> = {
	// gmail: mdiEmailOutline,           // descontinuado
	// calendar: mdiCalendarBlankOutline, // descontinuado
	// drive: mdiFileOutline,             // descontinuado
	outlook: mdiEmailOutline,
	mscalendar: mdiCalendarBlankOutline,
	onedrive: mdiFileOutline,
	teams: mdiNoteEditOutline,
	notion: mdiNoteEditOutline,
	slack: mdiSlack,
	meta: mdiFacebook,
	linkedin: mdiLinkedin,
	stripe: mdiCreditCardOutline,
	canva: mdiPalette,
	adobe: mdiImageMultipleOutline,
	trello: mdiViewColumnOutline,
	ffmpeg: mdiFilmstripBoxMultiple,
	youtube: mdiYoutube,
};

const COLOR_CLASS_BY_MCP: Record<string, string> = {
	// gmail: styles.mcpGmail,       // descontinuado
	// calendar: styles.mcpCalendar, // descontinuado
	// drive: styles.mcpDrive,       // descontinuado
	outlook: styles.mcpOutlook,
	mscalendar: styles.mcpMsCalendar,
	onedrive: styles.mcpOneDrive,
	teams: styles.mcpTeams,
	notion: styles.mcpNotion,
	slack: styles.mcpSlack,
	meta: styles.mcpMeta,
	linkedin: styles.mcpLinkedin,
	stripe: styles.mcpStripe,
	canva: styles.mcpCanva,
	adobe: styles.mcpAdobe,
	trello: styles.mcpTrello,
	ffmpeg: styles.mcpFFmpeg,
	youtube: styles.mcpYoutube,
};

const REVIEW_LABEL: Record<TaskDeliverableDTO["reviewStatus"], string> = {
	pending: "Pendente",
	approved: "Aprovado",
	rejected: "Rejeitado",
	revised: "Revisão solicitada",
};

const REVIEW_BADGE_CLASS: Record<TaskDeliverableDTO["reviewStatus"], string> = {
	pending: styles.reviewBadgePending,
	approved: styles.reviewBadgeApproved,
	rejected: styles.reviewBadgeRejected,
	revised: styles.reviewBadgeRevised,
};

export const DeliverableList: React.FC<DeliverableListProps> = ({
	workspaceId,
	taskId,
	subtaskId,
	refreshKey,
}) => {
	const [items, setItems] = useState<TaskDeliverableDTO[]>([]);
	const [loading, setLoading] = useState(false);
	const [reviewingId, setReviewingId] = useState<string | null>(null);
	const [revisionId, setRevisionId] = useState<string | null>(null);
	const [revisionNotes, setRevisionNotes] = useState("");
	const [diffMode, setDiffMode] = useState(false);
	// EK-52 / RF013: diff entre a ultima versao e a anterior (lazy load via toggle)
	const [diffData, setDiffData] = useState<DeliverableDiffDTO | null>(null);
	const [diffLoading, setDiffLoading] = useState(false);

	useEffect(() => {
		let active = true;
		setLoading(true);
		TeamLeaderServices.listSubtaskDeliverables(workspaceId, taskId, subtaskId)
			.then((data) => {
				if (active) setItems(data);
			})
			.catch((err) => {
				if (active) {
					NotistackServices.showErrorMessage(
						HandleError.isHandleError(err) ? err.message : "Erro ao carregar entregáveis",
					);
				}
			})
			.finally(() => {
				if (active) setLoading(false);
			});
		return () => {
			active = false;
		};
	}, [workspaceId, taskId, subtaskId, refreshKey]);

	// EK-52 / RF013: busca o diff (ultima vs anterior) quando o usuario ativa o modo.
	useEffect(() => {
		if (!diffMode) {
			setDiffData(null);
			return;
		}
		// Precisa de pelo menos 2 versoes para haver diff
		const distinctVersions = new Set(items.map((d) => d.version)).size;
		if (distinctVersions < 2) {
			setDiffData(null);
			return;
		}
		let active = true;
		setDiffLoading(true);
		TeamLeaderServices.getDeliverableDiff(workspaceId, taskId, subtaskId)
			.then((data) => {
				if (active) setDiffData(data);
			})
			.catch((err) => {
				if (active) {
					NotistackServices.showErrorMessage(
						HandleError.isHandleError(err) ? err.message : "Erro ao gerar diff de versoes",
					);
				}
			})
			.finally(() => {
				if (active) setDiffLoading(false);
			});
		return () => {
			active = false;
		};
	}, [diffMode, items, workspaceId, taskId, subtaskId]);

	const handleReview = async (deliverableId: string, action: DeliverableReviewAction, notes?: string) => {
		setReviewingId(deliverableId);
		try {
			const updated = await TeamLeaderServices.reviewDeliverable(workspaceId, taskId, deliverableId, {
				action,
				notes: notes ?? null,
			});
			setItems((prev) => prev.map((d) => (d.id === deliverableId ? updated : d)));
			setRevisionId(null);
			setRevisionNotes("");
		} catch (err) {
			NotistackServices.showErrorMessage(
				HandleError.isHandleError(err) ? err.message : "Erro ao revisar entregável",
			);
		} finally {
			setReviewingId(null);
		}
	};

	const handleRollback = async (deliverableId: string, version: number) => {
		if (!window.confirm(`Restaurar a versão ${version} como versão atual?\n\nIsso cria uma nova versão (cópia da v${version}) sem desfazer execuções externas (e-mails, eventos, etc).`)) {
			return;
		}
		setReviewingId(deliverableId);
		try {
			const created = await TeamLeaderServices.rollbackDeliverable(workspaceId, taskId, deliverableId);
			setItems((prev) => [created, ...prev]);
		} catch (err) {
			NotistackServices.showErrorMessage(
				HandleError.isHandleError(err) ? err.message : "Erro ao fazer rollback",
			);
		} finally {
			setReviewingId(null);
		}
	};

	if (loading && items.length === 0) {
		return (
			<div className={styles.loading}>
				<CircularProgress size={14} />
				<span>Carregando entregáveis...</span>
			</div>
		);
	}

	if (items.length === 0) return null;

	const groupedByVersion = items.reduce<Record<number, TaskDeliverableDTO[]>>((acc, d) => {
		(acc[d.version] = acc[d.version] || []).push(d);
		return acc;
	}, {});
	const versions = Object.keys(groupedByVersion)
		.map(Number)
		.sort((a, b) => b - a);
	const hasMultipleVersions = versions.length > 1;

	return (
		<div className={styles.list}>
			<div className={styles.headerRow}>
				<div className={styles.header}>📦 Entregáveis ({items.length})</div>
				{hasMultipleVersions && (
					<button
						type="button"
						className={clsx(styles.diffToggle, diffMode && styles.diffToggleActive)}
						onClick={() => setDiffMode(!diffMode)}
					>
						{diffMode ? "Ocultar comparação" : "Comparar versões"}
					</button>
				)}
			</div>

			{/* EK-52 / RF013: painel de comparacao entre a ultima versao e a anterior */}
			{diffMode && (
				<div className={styles.diffPanel}>
					{diffLoading && (
						<div className={styles.loading}>
							<CircularProgress size={14} />
							<span>Comparando v{versions[0]} com v{versions[1]}...</span>
						</div>
					)}
					{!diffLoading && diffData && (
						<>
							<div className={styles.diffHeader}>
								Comparacao: <strong>v{diffData.baseVersion}</strong> → <strong>v{diffData.targetVersion}</strong>
								{diffData.changedFields.length === 0 && (
									<span className={styles.diffNoChanges}>Nenhuma diferença encontrada</span>
								)}
							</div>
							{diffData.changedFields.length > 0 && (
								<>
									<div className={styles.diffChips}>
										{diffData.changedFields.map((f) => (
											<span key={f} className={styles.diffChip}>{f}</span>
										))}
									</div>
									{diffData.fields
										.filter((f) => f.changed && Array.isArray(f.changes))
										.map((f) => (
											<DiffFieldView key={f.field} field={f} />
										))}
								</>
							)}
						</>
					)}
				</div>
			)}

			<div className={diffMode ? styles.gridDiff : undefined}>
				{versions.map((v) => (
					<div key={v} className={clsx(styles.versionGroup, diffMode && styles.versionGroupDiff)}>
						{(hasMultipleVersions || diffMode) && (
							<div className={styles.versionLabel}>
								Versão {v} {v === versions[0] ? "(atual)" : ""}
							</div>
						)}
						{groupedByVersion[v].map((d) => {
							const icon = d.mcpSlug ? ICON_BY_MCP[d.mcpSlug] ?? mdiTextBoxOutline : mdiTextBoxOutline;
							const colorClass = d.mcpSlug ? COLOR_CLASS_BY_MCP[d.mcpSlug] ?? "" : styles.mcpGeneric;
							const isCurrentVersion = v === versions[0];
							const isBusy = reviewingId === d.id;
							return (
								<div key={d.id} className={styles.item}>
									<div className={clsx(styles.iconBox, colorClass)}>
										<Icon path={icon} size={0.8} />
									</div>
									<div className={styles.itemBody}>
										<div className={styles.itemTitleRow}>
											<span className={styles.itemTitle}>{d.title ?? "Entregável"}</span>
											{d.mcpSlug && <span className={styles.mcpChip}>{d.mcpSlug}</span>}
											<span className={clsx(styles.reviewBadge, REVIEW_BADGE_CLASS[d.reviewStatus])}>
												{REVIEW_LABEL[d.reviewStatus]}
											</span>
										</div>
										{d.preview && <div className={styles.itemPreview}>{d.preview}</div>}
										{d.reviewNotes && (
											<div className={styles.reviewNotes}>
												<strong>Nota:</strong> {d.reviewNotes}
											</div>
										)}
										<div className={styles.itemFooter}>
											{d.externalId && (
												<span className={styles.externalId}>
													<Icon path={mdiCheckCircleOutline} size={0.5} />
													ID: {d.externalId.slice(0, 16)}
													{d.externalId.length > 16 ? "…" : ""}
												</span>
											)}
											{d.externalUrl && (
												<a
													href={d.externalUrl}
													target="_blank"
													rel="noopener noreferrer"
													className={styles.externalLink}
												>
													<Icon path={mdiOpenInNew} size={0.55} /> Abrir
												</a>
											)}
										</div>

										{revisionId === d.id ? (
											<div className={styles.revisionForm}>
												<textarea
													className={styles.revisionInput}
													placeholder="Descreva o que precisa ser ajustado (>=3 caracteres)"
													value={revisionNotes}
													onChange={(e) => setRevisionNotes(e.target.value)}
													rows={2}
													autoFocus
												/>
												<div className={styles.revisionActions}>
													<button
														type="button"
														className={styles.btnSecondary}
														onClick={() => {
															setRevisionId(null);
															setRevisionNotes("");
														}}
														disabled={isBusy}
													>
														Cancelar
													</button>
													<button
														type="button"
														className={styles.btnPrimary}
														onClick={() => handleReview(d.id, "request_revision", revisionNotes)}
														disabled={isBusy || revisionNotes.trim().length < 3}
													>
														Solicitar revisão
													</button>
												</div>
											</div>
										) : (
											<div className={styles.reviewActions}>
												{d.reviewStatus === "pending" && (
													<>
														<Tooltip title="Aprovar entregável">
															<IconButton
																size="small"
																className={styles.actionApprove}
																onClick={() => handleReview(d.id, "approve")}
																disabled={isBusy}
															>
																<Icon path={mdiCheckBold} size={0.65} />
															</IconButton>
														</Tooltip>
														<Tooltip title="Rejeitar entregável">
															<IconButton
																size="small"
																className={styles.actionReject}
																onClick={() => {
																	const notes = window.prompt("Motivo da rejeição (>=3 caracteres):");
																	if (notes && notes.trim().length >= 3) {
																		handleReview(d.id, "reject", notes.trim());
																	}
																}}
																disabled={isBusy}
															>
																<Icon path={mdiClose} size={0.65} />
															</IconButton>
														</Tooltip>
														<Tooltip title="Solicitar revisão">
															<IconButton
																size="small"
																className={styles.actionRevise}
																onClick={() => setRevisionId(d.id)}
																disabled={isBusy}
															>
																<Icon path={mdiPencilOutline} size={0.65} />
															</IconButton>
														</Tooltip>
													</>
												)}
												{!isCurrentVersion && (
													<Tooltip title={`Restaurar v${v} como versão atual`}>
														<IconButton
															size="small"
															className={styles.actionRollback}
															onClick={() => handleRollback(d.id, v)}
															disabled={isBusy}
														>
															<Icon path={mdiUndoVariant} size={0.65} />
														</IconButton>
													</Tooltip>
												)}
												{isBusy && <CircularProgress size={14} />}
											</div>
										)}
									</div>
								</div>
							);
						})}
					</div>
				))}
			</div>
		</div>
	);
};

// EK-52 / RF013: visualizacao inline de um campo com diff de texto/JSON.
// added = verde, removed = vermelho, unchanged = cor padrao.
const DiffFieldView: React.FC<{ field: FieldDiff }> = ({ field }) => {
	if (!Array.isArray(field.changes) || field.changes.length === 0) return null;
	return (
		<div className={styles.diffField}>
			<div className={styles.diffFieldLabel}>{field.field}</div>
			<pre className={styles.diffFieldBody}>
				{field.changes.map((c, i) => (
					<span
						key={i}
						className={clsx(
							styles.diffSegment,
							c.added && styles.diffAdded,
							c.removed && styles.diffRemoved,
							!c.added && !c.removed && styles.diffUnchanged,
						)}
					>
						{c.value}
					</span>
				))}
			</pre>
		</div>
	);
};
